/**
 * Numeric take-grade entry (backlog: numeric-take-grades).
 *
 * The owner wanted 0-100 numeric grade entry for calibration takes — a
 * coach's real gradebook has entries like "85" and "90-94", which the old
 * letter-only A/B/C/D/F picker couldn't express. Built ADDITIVELY: the two
 * calibration-take forms (CalibrationTakeUploader.tsx,
 * CalibrationTakeEditForm.tsx) now show a number input instead of the
 * letter `<select>`; `CalibrationTake.grade_label` (the legacy field) stays
 * exactly as-is for every take graded before this change — not backfilled,
 * not silently converted.
 *
 * Precedence (mirrors the backend's `grade_scale.resolve_grade_numeric()`):
 * numeric wins when set, else fall back to the letter's band midpoint, else
 * ungraded. This module owns the FRONTEND-ONLY half of that story — purely
 * presentational helpers (the live band-letter chip, the take-card badge
 * text, the legacy-take placeholder value). No network calls, no state.
 *
 * Band edges mirror `BAND_MIDPOINTS` / the owner's "Grade bands" ruling in
 * backend/app/services/grade_scale.py — A: 90-100, B: 80-89, C: 70-79,
 * D: 60-69, F: <60. Single source of provenance; do not re-derive these
 * numbers independently elsewhere in the frontend.
 */

import type { CalibrationGradeLabel } from '@/types/calibrationTake';

/** Coach-facing 0-100 numeric grade scale bounds — mirrors the backend's
 * `grade_scale.GRADE_NUMERIC_MIN`/`GRADE_NUMERIC_MAX`. */
export const GRADE_NUMERIC_MIN = 0;
export const GRADE_NUMERIC_MAX = 100;

/**
 * Representative display value for each legacy letter grade — mirrors the
 * backend's `BAND_MIDPOINTS` (app/services/grade_scale.py: A=95, B=85,
 * C=75, D=65, F=55). Frontend-only, used SOLELY to populate the edit form's
 * placeholder text when a legacy letter-only take's numeric input is left
 * empty — NEVER sent to the API, and never used to silently convert a
 * coach's letter into a fake-precision number on an unrelated save.
 */
export const GRADE_LABEL_MIDPOINTS: Record<CalibrationGradeLabel, number> = {
  A: 95,
  B: 85,
  C: 75,
  D: 65,
  F: 55,
};

/**
 * Compute the plain band LETTER (A-F, matching the backend's
 * `VALID_GRADE_LABELS` vocabulary) for a numeric 0-100 grade — a display
 * convenience only (never stored). Coach-facing display surfaces (the
 * "Reads as" chip and the take-card badge) use
 * `gradeNumericToDisplayGrade()` below, which refines this with +/-.
 *
 * Band edges are the same 60/90/70/80 boundaries the backend's
 * `grade_scale._INTERIOR_LO`/`_INTERIOR_HI` and `BAND_MIDPOINTS` encode —
 * not re-derived, just mirrored for display.
 */
export function gradeNumericToBandLetter(value: number): CalibrationGradeLabel {
  if (value >= 90) return 'A';
  if (value >= 80) return 'B';
  if (value >= 70) return 'C';
  if (value >= 60) return 'D';
  return 'F';
}

/**
 * Coach-facing DISPLAY grade with +/- refinement — "82 reads as B-",
 * "99 reads as A+" (owner ruling 2026-09-02). Display-only, never stored,
 * never sent to the API: the backend's band vocabulary stays plain A-F
 * (`VALID_GRADE_LABELS`) and the fitter consumes the raw number anyway, so
 * the modifier exists purely to make the chip/badge read like a gradebook.
 *
 * Ranges subdivide the existing 10-point bands by the conventional US
 * school scheme — within each of A/B/C/D: minus = bottom 3 points
 * (x0-x2.99), plain = middle 4 (x3-x6.99), plus = top 3 (x7-x9.99). A+
 * therefore covers 97-100. F takes no modifier (convention; there is no
 * meaningful F+ / F-).
 */
export function gradeNumericToDisplayGrade(value: number): string {
  const letter = gradeNumericToBandLetter(value);
  if (letter === 'F') return 'F';
  const bandBase = { A: 90, B: 80, C: 70, D: 60 }[letter];
  const offset = value - bandBase;
  // 100 falls out naturally: offset 10 >= 7 → "A+".
  if (offset >= 7) return `${letter}+`;
  if (offset < 3) return `${letter}-`;
  return letter;
}

/** True when `value` is a finite number inside [GRADE_NUMERIC_MIN, GRADE_NUMERIC_MAX]. */
export function isValidGradeNumeric(value: number | null): value is number {
  return (
    value !== null &&
    Number.isFinite(value) &&
    value >= GRADE_NUMERIC_MIN &&
    value <= GRADE_NUMERIC_MAX
  );
}

/**
 * Coach-facing rendering of a numeric grade — "87", not "87.0"; preserves a
 * genuine decimal ("87.5") when the coach typed one. Mirrors the backend's
 * `grade_scale.format_grade_numeric_for_display()` — one formatting rule
 * shared by both surfaces, matching this repo's own lesson about
 * independently-maintained vocabulary drift (2026-08-09 rule-compiler
 * stitch incident).
 */
export function formatGradeNumericValue(value: number): string {
  // JS number->string coercion already drops a trailing ".0" for a whole
  // number (String(87.0) === "87"), so rounding to 2dp and stringifying is
  // sufficient — no separate integer-vs-decimal branch needed.
  const rounded = Math.round(value * 100) / 100;
  return String(rounded);
}

/**
 * Parse a raw number-input string into a grade value for submission.
 * Returns null for an empty/whitespace-only string (the "leave ungraded /
 * unchanged" case), otherwise the parsed number (which may be out of range
 * or NaN — callers validate separately with `isValidGradeNumeric` before
 * treating it as a submittable value).
 */
export function parseGradeNumericInput(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  return Number(trimmed);
}

/**
 * Take-card badge text for a calibration take's grade (CalibrationTakeList.tsx).
 * Numeric wins when set — "87 · B+" — completely ignoring any stored legacy
 * `grade_label` on that same take (a numeric override during edit is not
 * required to also clear/update the legacy letter; the badge simply never
 * reads it once a number is present, so there is no stale/contradictory
 * letter to reconcile). Falls back to the plain legacy letter ("B") when no
 * numeric grade is set. Returns null when the take has neither.
 */
export function formatCalibrationGradeBadge(take: {
  grade_numeric?: number | null;
  grade_label?: CalibrationGradeLabel | string | null;
}): string | null {
  if (take.grade_numeric != null) {
    const letter = gradeNumericToDisplayGrade(take.grade_numeric);
    return `${formatGradeNumericValue(take.grade_numeric)} · ${letter}`;
  }
  if (take.grade_label) {
    return take.grade_label;
  }
  return null;
}
